/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import cx from 'classnames';
import * as React from 'react';
import { ProgressRadial } from '../ProgressIndicators/index.js';
import { useMergedRefs, useTheme } from '../utils/index.js';
import '@itwin/itwinui-css/css/checkbox.css';
/**
 * Simple input checkbox
 * @example
 * <Checkbox />
 * <Checkbox label='Basic Checkbox' />
 * <Checkbox label='Disabled Checkbox' disabled />
 * <Checkbox label='Checked' checked />
 * <Checkbox label='Positive Checkbox' status='positive' />
 * <Checkbox label='Warning Checkbox' status='warning' />
 * <Checkbox label='Negative Checkbox' status='negative' />
 * <Checkbox label='Visibility Checkbox' variant='eyeball' />
 */
export const Checkbox = React.forwardRef((props, ref) => {
    const { className, disabled = false, indeterminate = false, label, status, variant = 'default', setFocus, isLoading = false, style, ...rest } = props;
    useTheme();
    const inputElementRef = React.useRef(null);
    const refs = useMergedRefs(inputElementRef, ref);
    React.useEffect(() => {
        if (inputElementRef.current && setFocus) {
            inputElementRef.current.focus();
        }
    }, [setFocus]);
    React.useEffect(() => {
        if (inputElementRef.current) {
            inputElementRef.current.indeterminate = indeterminate;
            inputElementRef.current.checked = indeterminate
                ? false
                : inputElementRef.current.checked;
        }
    });
    const checkbox = (React.createElement(React.Fragment, null,
        React.createElement("input", { className: cx('iui-checkbox', {
                'iui-checkbox-visibility': variant === 'eyeball',
                'iui-loading': isLoading,
            }, className && { [className]: !label }), style: !label ? style : undefined, disabled: disabled || isLoading, type: 'checkbox', ref: refs, ...rest }),
        isLoading && React.createElement(ProgressRadial, { size: 'x-small', indeterminate: true })));
    return !label ? (checkbox) : (React.createElement("label", { className: cx('iui-checkbox-wrapper', {
            'iui-disabled': disabled,
            [`iui-${status}`]: !!status,
            'iui-loading': isLoading,
        }, className), style: style },
        checkbox,
        label && React.createElement("span", { className: 'iui-checkbox-label' }, label)));
});
export default Checkbox;
